import React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import {
  createPageLikeThis,
  crossSellBannerImpression,
  tipsPromptClosed,
  PageState,
} from 'src/redux/modules/page';
import { AppState } from 'src/redux/modules';
import { SupporterResource } from 'src/helpers/api/activities';
import { StoryNode } from 'src/helpers/api/pages';
import { UpdateResource } from 'src/helpers/api/updates';
import renderRoutes, { RouteConfig } from 'src/helpers/renderRoutes';
import { isClosed } from 'src/helpers/page';
import PageHeaderContainer from 'src/containers/page/PageHeaderContainer';
import AlternativeProjectsContainer from 'src/containers/page/AlternativeProjectsContainer';
import EncourageShareContainer from 'src/containers/page/EncourageShareContainer';
import SupportersContainer from 'src/containers/page/SupportersContainer';
import AboutFundraiserContainer from 'src/containers/page/AboutFundraiserContainer';
import MetaContainer from 'src/containers/page/MetaContainer';
import SocialShareContainer from 'src/containers/page/SocialShareContainer';
import UpdatesContainer from 'src/containers/page/UpdatesContainer';
import PageFooterContainer from 'src/containers/page/PageFooterContainer';
import PageWrapper from 'src/components/Page/PageWrapper';
import PageContentWrapper from 'src/components/Page/PageContentWrapper';
import PageContent from 'src/components/Page/PageContent';
import PageSidebar from 'src/components/Page/PageSidebar';
import SupportMessage from 'src/components/SupportMessage/SupportMessage';
import { TranslateYTransition } from 'src/components/Transitions';
import RichStory from 'src/components/RichStory';
import CrossSellBanner from 'src/components/CrossSellBanner/CrossSellBanner';
import LastUpdated from 'src/components/LastUpdated/LastUpdated';
import StickyHeaderDonorContainer from 'src/containers/page/StickyHeaderDonorContainer';
import { enrol } from 'src/helpers/api/ab';
import { getUpdates } from 'src/redux/modules/richUpdates';
import { withRouter, RouteComponentProps } from 'react-router';
import { scrollToUpdate as scrollToUpdateAction } from 'src/redux/modules/richUpdates/actions';
import { setScrollRestoration } from 'src/helpers/browser';
import { getPageSettings } from 'src/redux/modules/settings/actions';
import { getCharityInfo } from 'src/redux/modules/charity/actions';
import { getPageSection } from 'src/helpers/analytics';
import promptRulesEngine from 'src/helpers/prompts';
import RecurringDonationsAwarenessBannerContainer from 'src/containers/page/RecurringDonationsAwarenessBannerContainer';
import WhatIsCrowdfunding from 'src/components/WhatIsCrowdfunding/WhatIsCrowdfunding';

type MatchParams = {
  name: string;
  updateId?: string;
};

type OwnProps = RouteComponentProps<MatchParams> & { route: RouteConfig };

interface StateProps {
  page: PageState;
  supporters: SupporterResource[];
  isPageClosed: boolean;
  socialShareUrl: string;
  id: string;
  isLoadingUpdates: boolean;
  updates: UpdateResource[];
  richStory?: StoryNode[];
  isSharePromptsOpen: boolean;
  isOwnerRequested: boolean;
  appState: AppState;
  isCreateWizardOpen: boolean;
  supportsRecurringDonations: boolean;
}

const mapStateToProps = (state: AppState): StateProps => {
  return {
    id: state.page.id,
    isPageClosed: isClosed(state.page),
    page: state.page,
    supporters: state.supporters.items,
    socialShareUrl: state.page.socialShareUrl,
    isLoadingUpdates: state.richUpdates.isLoading,
    updates: getUpdates(state.richUpdates),
    isSharePromptsOpen: state.sharePrompts.isSharePromptsOpen,
    richStory: state.page.richStory,
    isOwnerRequested: state.page.isOwnerRequested,
    appState: state,
    isCreateWizardOpen: state.page.isCreateWizardOpen,
    supportsRecurringDonations: state.settings.supportsRecurringDonations,
  };
};

interface DispatchProps {
  onPageMount: (page: PageState) => void;
  tipsPromptClosed: () => void;
  createPageLikeThis: () => void;
  crossSellBannerImpression: () => void;
  scrollToUpdate: () => void;
  getPageSettings: () => void;
  getCharityInfo: (charityId: number) => void;
  dispatch: Dispatch<AppState>;
}

const mapDispatchToProps = (dispatch: any, props: OwnProps): DispatchProps => {
  return {
    ...bindActionCreators(
      {
        // TODO: remove once analytics middleware gets enabled server-side
        onPageMount: page => ({ type: 'PAGE_MOUNT', payload: page }),
        tipsPromptClosed,
        createPageLikeThis,
        crossSellBannerImpression,
        scrollToUpdate: scrollToUpdateAction,
        getPageSettings,
        getCharityInfo,
      },
      dispatch
    ),
    dispatch,
  };
};

export type Props = OwnProps & StateProps & DispatchProps;

interface State {
  isStickyHeaderEnabled: boolean;
  promptTransitionActive?: boolean;
  promptHeight?: number;
}

export class PageView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = { isStickyHeaderEnabled: false };
  }

  async componentDidMount() {
    const {
      page,
      onPageMount,
      match,
      scrollToUpdate,
      isPageClosed,
    } = this.props;
    const isStickyHeaderEnabled = (await enrol('cf_sticky_header')) === 'B';

    // This is used by analytics for server-side rendered pages, which don't
    // receive PAGE_SUCCESS on the first page load on the client, but still
    // need to send analytics events like "page view".
    // TODO: remove once analytics middleware gets enabled server-side
    onPageMount(page);
    this.setState({ isStickyHeaderEnabled });

    if (match.params.updateId !== undefined) {
      setScrollRestoration('manual');
      scrollToUpdate();
    }

    if (!isPageClosed) {
      this.props.getPageSettings();
    }

    if (page.charityId) {
      this.props.getCharityInfo(page.charityId);
    }
  }

  componentWillUnmount() {
    setScrollRestoration('auto');
  }

  activatePromptTransition = ({ height }: { height: number }) => {
    this.setState({ promptTransitionActive: true, promptHeight: height });
  };

  closePrompt = () => {
    this.props.tipsPromptClosed();
    this.setState({ promptTransitionActive: false });
  };

  render() {
    const {
      page,
      route,
      isPageClosed,
      socialShareUrl,
      richStory,
      isOwnerRequested,
      supporters,
      updates,
      isCreateWizardOpen,
      supportsRecurringDonations,
    } = this.props;
    const {
      promptTransitionActive,
      promptHeight,
      isStickyHeaderEnabled,
    } = this.state;
    const showStickyHeader =
      !isOwnerRequested && !isPageClosed && isStickyHeaderEnabled;

    const prompt = promptRulesEngine(this.props.appState, isOwnerRequested, {
      dispatch: this.props.dispatch,
      pageSection: getPageSection(this.props.location.pathname),
      activatePromptTransition: this.activatePromptTransition,
      closePrompt: this.closePrompt,
      promptTransitionActive: this.state.promptTransitionActive,
    });

    return (
      <>
        <MetaContainer />

        {showStickyHeader && <StickyHeaderDonorContainer />}

        <div style={{ marginBottom: promptHeight }}>
          {!isCreateWizardOpen && <section>{prompt}</section>}

          <TranslateYTransition
            active={promptTransitionActive}
            endOffset={promptHeight}
            duration="0.5s"
            timingFunction="ease-in-out"
          >
            <PageWrapper>
              <PageHeaderContainer />

              <PageContentWrapper>
                <PageContent>
                  <RichStory storyNodes={richStory} />
                  {supportsRecurringDonations && (
                    <div className="jg-space-mhmd jg-space-mhxl@lg jg-space-mbml jg-space-mbsm@lg">
                      <RecurringDonationsAwarenessBannerContainer />
                    </div>
                  )}
                  <SocialShareContainer />
                  <UpdatesContainer />
                  <SupportMessage socialShareUrl={socialShareUrl} />
                  <LastUpdated
                    createdAt={page.createdAt}
                    activatedAt={page.activatedAt!}
                    supporters={supporters}
                    updates={updates}
                  />
                </PageContent>

                <PageSidebar>
                  <SupportersContainer />
                  {supportsRecurringDonations && (
                    <WhatIsCrowdfunding className="jg-bdt jg-bd--very-light-grey jg-space-ptlg jg-space-pmd jg-space-mtlg jg-space-mbmd" />
                  )}
                </PageSidebar>
              </PageContentWrapper>
            </PageWrapper>

            <AlternativeProjectsContainer />
            {isPageClosed && (
              <CrossSellBanner
                crossSellBannerImpression={this.props.crossSellBannerImpression}
                createPageLikeThis={this.props.createPageLikeThis}
              />
            )}
            <div className="jg-bdt jg-bd--very-light-grey jg-space-pms jg-space-pblg">
              <AboutFundraiserContainer />
            </div>
            <EncourageShareContainer />
          </TranslateYTransition>
          <PageFooterContainer />
        </div>
        {renderRoutes(route.routes)}
      </>
    );
  }
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(PageView)
);



// WEBPACK FOOTER //
// ./src/containers/page/PageView.tsx